import { useEffect, useMemo } from 'react';
import { Box, MenuItem, Select, styled, useTheme } from '@mui/material';

import { NETWORK_SELECTION, SUPPORTED_NETWORKS, DEFAULT_NETWORK } from 'constants/index';
import useWalletContext from 'hooks/useWalletContext';

const NetworkIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'background' && prop !== 'iconFill',
})(({ background, iconFill }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: 37,
  minWidth: 40,
  borderRadius: 0,
  background,
  overflow: 'hidden',

  '& svg': {
    width: 38,
    height: 38,
    '& path': iconFill ? { fill: iconFill } : undefined,
  },
}));

// Network styling: background color for selected icon, plus optional selectedFill override
// All networks use their native SVG colors; selectedFill is only needed when icon color matches background (e.g. Ethereum)
const getNetworkStyles = () => ({
  energi: {
    background: '#27ff97',
  },
  [SUPPORTED_NETWORKS.Ethereum]: {
    background: '#627EEA',
    selectedFill: 'white', // White icon on blue background
  },
  [SUPPORTED_NETWORKS.ApeChain]: {
    background: '#FFFFFF',
  },
  [SUPPORTED_NETWORKS.Arbitrum]: {
    background: '#2d374b',
  },
  [SUPPORTED_NETWORKS.Monad]: {
    background: '#6E54FF',
  },
  [SUPPORTED_NETWORKS.Base]: {
    background: '#0052FF',
    selectedFill: 'white', // White icon on blue background
  },
  [SUPPORTED_NETWORKS.Abstract]: {
    background: '#10c872',
  },
  [SUPPORTED_NETWORKS.Bsc]: {
    background: '#F3BA2F',
    selectedFill: 'white',
  },
});

const NetworkSelect = () => {
  const NETWORK_STYLES = useMemo(() => getNetworkStyles(), []);
  const theme = useTheme();
  const {
    isConnected,
    globalChain,
    chainId,
    storedChain,
    updateStoredChain,
    switchToNetwork,
    setWrongNetwork,
  } = useWalletContext();

  const handleToggleNetwork = async (targetChain) => {
    try {
      if (globalChain !== targetChain) {
        let switchResult;
        if (isConnected) {
          switchResult = await switchToNetwork(targetChain);
        }
        if (switchResult?.id === targetChain) {
          updateStoredChain(targetChain);
          window.location.href = '/lp-bonds';
          return;
        }

        updateStoredChain(targetChain);
        window.location.href = '/lp-bonds';
      }
    } catch (error) {
      console.error(error);
    }
  };

  const selectedNetwork = useMemo(() => {
    if (!Object.values(SUPPORTED_NETWORKS).includes(storedChain)) {
      updateStoredChain(DEFAULT_NETWORK);
      return NETWORK_SELECTION.find((item) => item.value === DEFAULT_NETWORK);
    }
    return NETWORK_SELECTION.find((item) => item.value === storedChain);
  }, [storedChain, updateStoredChain]);

  const SelectedIcon = useMemo(() => {
    const styles = NETWORK_STYLES[selectedNetwork.value];
    return (
      <NetworkIcon background={styles.background} iconFill={styles.selectedFill}>
        {selectedNetwork.icon}
      </NetworkIcon>
    );
  }, [selectedNetwork]);

  useEffect(() => {
    if (isConnected && chainId !== storedChain) {
      const supportedChainIds = Object.values(SUPPORTED_NETWORKS);
      if (supportedChainIds.includes(chainId)) {
        localStorage.setItem('storedChain', chainId);
        setWrongNetwork(false);
        // Stay on current page if on /get-gmi, otherwise redirect to /lp-bonds
        if (!window.location.pathname.startsWith('/get-gmi')) {
          window.location.href = '/lp-bonds';
        }
        return;
      }
      setWrongNetwork(true);
    } else {
      setWrongNetwork(false);
    }
  }, [isConnected, chainId, storedChain]);

  return (
    <Select
      variant="standard"
      value={storedChain}
      disableUnderline
      IconComponent={() => null}
      onChange={(event) => {
        const selected = NETWORK_SELECTION.find((n) => n.value === event.target.value);
        if (selected?.isExternal) {
          window.location.href = selected.url;
          return;
        }
        handleToggleNetwork(event.target.value);
      }}
      renderValue={() => SelectedIcon}
      sx={{
        height: '100%',
        width: '40px',
        '& .MuiSelect-select': {
          padding: 0,
          display: 'flex',
          alignItems: 'center',
        },
      }}
      inputProps={{
        MenuProps: {
          disableScrollLock: true,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'right',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'right',
          },
          MenuListProps: {
            sx: {
              padding: 0,
              borderRadius: '8px',
            },
          },
          PaperProps: {
            sx: {
              borderRadius: '8px',
              border: `1px solid ${theme.palette.border.separator}`,
            },
          },
        },
      }}
    >
      {NETWORK_SELECTION.map((network) => (
        <MenuItem
          key={network.value}
          value={network.value}
          sx={{
            backgroundColor: `${
              network.value === selectedNetwork.value
                ? theme.palette.background.select
                : theme.palette.background.default
            } !important`,
            padding: '12.5px 10px',

            '&: hover': {
              backgroundColor: `${
                network.value !== selectedNetwork.value && theme.palette.background.hover
              } !important`,
            },
            '& > svg': {
              width: 28,
              height: 28,
            },
          }}
        >
          {network.icon}
          <Box
            component="span"
            sx={{
              fontWeight: 600,
              fontSize: '14px',
              letterSpacing: '0.15px',
            }}
          >
            {network.label}
          </Box>
        </MenuItem>
      ))}
    </Select>
  );
};

export default NetworkSelect;
